import {
  createLabEnterpriseMaster,
  modifyLabEnterpriseMaster,
  searchLabEnterpriseMasters,
  setLabEnterpriseActive,
  type LabEnterpriseInvestigation,
} from '@/modules/lab/api/labOperationalMasterApi'
import { parseCategory } from '@/modules/lab/catalog/catalogConstants'
import { recordCategoryUse, recordSpecimenUse, recordTestCreated } from '@/modules/lab/catalog/catalogOperatorPrefs'
import { enableAtHospital } from '@/modules/lab/catalog/api/catalogPilotApi'
import { markDraftMaster, setAliasesForMaster } from '@/modules/lab/catalog/catalogPilotMetaStore'
import type { CatalogInvestigationRow } from '@/modules/lab/catalog/types'

export const DUPLICATE_TEST_CODE_MESSAGE = 'Test code already exists. Please enter a different code.'

export type QuickCreateInput = {
  displayName: string
  investigationCode: string
  category: string
  specimenType: string
  billableFlag: boolean
  aliases: string[]
  /** @deprecated Use investigationCode. Kept so older callers still supply a code. */
  shortCode?: string
  hospitalCode: string
  asLocalVariant: boolean
}

type MutationErrorBody = {
  message?: string
  fieldErrors?: Record<string, string>
}

export function catalogMutationErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: MutationErrorBody } } | null)?.response?.data
  const raw = data?.message ?? (error instanceof Error ? error.message : '')
  if (raw.includes('Test code already exists') || raw.includes('LAB_MASTER_DUPLICATE_CODE')) {
    return DUPLICATE_TEST_CODE_MESSAGE
  }
  const field = data?.fieldErrors?.investigationCode || data?.fieldErrors?.displayName
  if (field) return field
  const stripped = raw.replace(/^LAB_[A-Z0-9_]+:\s*/, '')
  if (stripped && !stripped.startsWith('Request failed')) return stripped
  return 'Save failed'
}

export function investigationCreateErrorMessage(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: MutationErrorBody & { errorCode?: string } } } | null)
    ?.response
  const raw = response?.data?.message ?? ''
  const code = response?.data?.errorCode ?? ''
  if (
    response?.status === 409
    || code === 'LAB_MASTER_DUPLICATE_CODE'
    || raw.includes('Test code already exists')
    || raw.includes('LAB_MASTER_DUPLICATE_CODE')
  ) {
    return 'This investigation already exists.'
  }
  return catalogMutationErrorMessage(error)
}

function resolvePatchedCategory(patchCategory: string | undefined, existing: string): string {
  if (patchCategory === undefined) return existing
  const category = parseCategory(patchCategory)
  if (!category) throw new Error('Select a valid department')
  return category
}

function validateQuickCreateInput(input: QuickCreateInput): { category: string; specimen: string } {
  const category = parseCategory(input.category)
  const specimen = input.specimenType?.trim()
  if (!category) throw new Error('Select a test category before saving')
  if (!specimen) throw new Error('Select a specimen type before saving')
  return { category, specimen }
}

function resolveInvestigationCode(input: QuickCreateInput): string {
  const code = (input.investigationCode ?? input.shortCode ?? '').trim()
  if (!code) throw new Error('Enter a test code')
  if (code.length > 64) throw new Error('Test code must be 64 characters or fewer')
  return code
}

async function persistAliases(masterId: string, aliases: string[]): Promise<void> {
  if (aliases.length > 0) setAliasesForMaster(masterId, aliases)
}

export async function createLocalDraftInvestigation(input: QuickCreateInput): Promise<LabEnterpriseInvestigation> {
  const { category, specimen } = validateQuickCreateInput(input)
  const code = resolveInvestigationCode(input)

  const created = await createLabEnterpriseMaster({
    investigationCode: code,
    displayName: input.displayName.trim(),
    category,
    specimenType: specimen,
    billableFlag: input.billableFlag,
    activeFlag: 'I',
  })

  if (!created?.investigationMasterId) throw new Error('Could not create test')
  if (created.reactivated) return created

  markDraftMaster(created.investigationMasterId)
  await persistAliases(created.investigationMasterId, input.aliases)
  recordCategoryUse(category)
  recordSpecimenUse(specimen)
  recordTestCreated({
    masterId: created.investigationMasterId,
    name: created.displayName,
    code: created.investigationCode,
  })

  return created
}

export async function createAndEnableInvestigation(input: QuickCreateInput): Promise<LabEnterpriseInvestigation> {
  const { category, specimen } = validateQuickCreateInput(input)
  const code = resolveInvestigationCode(input)

  const created = await createLabEnterpriseMaster({
    investigationCode: code,
    displayName: input.displayName.trim(),
    category,
    specimenType: specimen,
    billableFlag: input.billableFlag,
    activeFlag: 'A',
  })

  if (!created?.investigationMasterId) throw new Error('Could not create test')
  if (created.reactivated) return created

  await persistAliases(created.investigationMasterId, input.aliases)
  recordCategoryUse(category)
  recordSpecimenUse(specimen)
  recordTestCreated({
    masterId: created.investigationMasterId,
    name: created.displayName,
    code: created.investigationCode,
  })

  await enableAtHospital({
    investigationMasterId: created.investigationMasterId,
    investigationCode: created.investigationCode,
    displayName: created.displayName,
    category: created.category,
    specimenType: specimen,
    billableFlag: input.billableFlag,
    activeFlag: 'A',
    mappingId: null,
    hospitalEnabled: false,
    billingServiceId: null,
    billingLinkStatus: 'PENDING',
    tariffAmount: null,
    localDisplayName: null,
    localCode: null,
    isLocal: code.toUpperCase().startsWith('LOCAL-'),
    isDraft: false,
    aliases: input.aliases,
  })

  return created
}

export async function activateDraftInvestigation(masterId: string): Promise<void> {
  await setLabEnterpriseActive(masterId, true)
}

export async function patchEnterpriseInvestigation(
  row: CatalogInvestigationRow,
  patch: Partial<Pick<CatalogInvestigationRow, 'displayName' | 'investigationCode' | 'specimenType' | 'billableFlag' | 'category'>>,
): Promise<LabEnterpriseInvestigation> {
  const existing = await searchLabEnterpriseMasters(row.investigationCode)
  const ent =
    existing.find((e) => e.investigationMasterId === row.investigationMasterId) ??
    ({
      investigationMasterId: row.investigationMasterId,
      investigationCode: row.investigationCode,
      displayName: row.displayName,
      category: row.category,
      specimenType: row.specimenType,
      billableFlag: row.billableFlag,
      activeFlag: row.activeFlag,
    } as LabEnterpriseInvestigation)

  const body: LabEnterpriseInvestigation = {
    ...ent,
    displayName: patch.displayName ?? ent.displayName,
    investigationCode: patch.investigationCode?.trim() || ent.investigationCode,
    category: resolvePatchedCategory(patch.category, ent.category),
    specimenType: patch.specimenType !== undefined ? patch.specimenType : ent.specimenType,
    billableFlag: patch.billableFlag ?? ent.billableFlag,
    // Search is the authoritative row, including the optimistic-lock token.
    // Do not reuse a department-edit snapshot that omitted opVersion.
    opVersion: ent.opVersion,
  }

  const updated = await modifyLabEnterpriseMaster(body)
  if (!updated) throw new Error('Update failed')
  return updated
}

export async function setInvestigationActive(row: CatalogInvestigationRow, active: boolean): Promise<void> {
  await setLabEnterpriseActive(row.investigationMasterId, active)
}
